import React from 'react';
import { R2FTableProps, PendingThresholdChange } from 'src/features/types/r2fTypes';
import { useR2FThresholdTableState } from './useR2FThresholdTableState';
import { R2FFilterControls } from './R2FFilterControls';
import { R2FTableHeader } from './R2FTableHeader';
import { R2FTableRows } from './R2FTableRows';
import { R2FSaveButton } from './R2FSaveButton';
import './styles.css';

/**
 * Main component for the R2F threshold table
 */
export const R2FThresholdTable: React.FC<R2FTableProps & {
    pendingChanges?: PendingThresholdChange[];
    warehouseId?: string;
}> = ({
    data,
    onSave,
    onDelete,
    onRemovePendingPC,
    userCanEdit,
    pendingChanges: externalPendingChanges,
    warehouseId
}) => {
    // Use the custom hook to manage state and handlers
    const {
        // State
        expandedProductionCapabilities,
        pendingChanges,
        invalidRows,
        filters,
        sorting,
        pendingChangeCount,
        isSaveDisabled,
        availableProductionCapabilities,
        availableHandlingTiers,
        filteredAndSortedPCs,
        
        // Handlers
        handleToggleExpand,
        handleValueChange,
        handleAddNode,
        handleDeleteNode,
        handleSave,
        handleFilterChange,
        handleSortChange,
    } = useR2FThresholdTableState({
        data,
        externalPendingChanges,
        userCanEdit,
        onSave
    });

    return (
        <div className="r2f-threshold-table-container">
            <R2FFilterControls 
                filters={filters}
                onFilterChange={handleFilterChange}
                availableProductionCapabilities={availableProductionCapabilities}
                availableHandlingTiers={availableHandlingTiers}
            />
            
            <div className="table-container">
                <table className="r2f-threshold-table">
                <R2FTableHeader 
                    sorting={sorting}
                    onSortChange={handleSortChange}
                    userCanEdit={userCanEdit}
                />
                    <tbody>
                        {filteredAndSortedPCs.length > 0 ? (
                            <R2FTableRows
                                productionCapabilities={filteredAndSortedPCs}
                                expandedProductionCapabilities={expandedProductionCapabilities}
                                pendingChanges={pendingChanges}
                                handleToggleExpand={handleToggleExpand}
                                handleValueChange={handleValueChange}
                                handleAddNode={handleAddNode}
                                handleDeleteNode={handleDeleteNode}
                                onDelete={onDelete || ((id, pc, reason, source) => Promise.resolve(false))}
                                onRemovePendingPC={onRemovePendingPC}
                                userCanEdit={userCanEdit}
                                warehouseId={warehouseId}
                            />
                        ) : (
                            <tr>
                                <td colSpan={userCanEdit ? 8 : 7} className="empty-table-message">
                                    {filters.productionCapability || filters.handlingTier || filters.status ? (
                                        <span>No results match your filter criteria</span>
                                    ) : (
                                        <span>No production capabilities available</span>
                                    )}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
            
            <R2FSaveButton 
                pendingChangeCount={pendingChangeCount}
                invalidRowsCount={invalidRows.size}
                isSaveDisabled={isSaveDisabled}
                onSave={handleSave}
                userCanEdit={userCanEdit}
            />
        </div>
    );
};
